import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable } from '@/components/shared/DataTable';
import { useCurrency } from '@/hooks/useCurrency';
import type {
  ForecastReviewClassificationBreakdownResponse,
  ForecastReviewClassificationBreakdownRow,
} from '@/services/forecastReviewService';
import { ColumnHeader, VariationBadge } from './ForecastReviewTableCells';
import { formatUiNumber } from '@/utils/uiLocale';

interface ClassificationBreakdownProps {
  data: ForecastReviewClassificationBreakdownResponse | null;
  loading?: boolean;
  viewType: 'units' | 'value';
  dimension: 'abc_quantity' | 'abc_value' | 'xyz_class' | 'adi_cv_class';
  onDimensionChange: (d: 'abc_quantity' | 'abc_value' | 'xyz_class' | 'adi_cv_class') => void;
  activeClassValue?: string | null;
  onApplyChartFilter?: (row: ForecastReviewClassificationBreakdownRow) => void;
}

function abcBadgeClass(abc: string): string {
  if (abc === 'A') return 'bg-emerald-100 text-emerald-800';
  if (abc === 'B') return 'bg-amber-100 text-amber-800';
  if (abc === 'C') return 'bg-gray-100 text-gray-700';
  return 'bg-gray-100 text-gray-600';
}

function xyzBadgeClass(xyz: string): string {
  if (xyz === 'X') return 'bg-blue-100 text-blue-800';
  if (xyz === 'Y') return 'bg-violet-100 text-violet-800';
  if (xyz === 'Z') return 'bg-orange-100 text-orange-800';
  return 'bg-gray-100 text-gray-600';
}

function adiCvBadgeClass(value: string): string {
  if (value === 'smooth') return 'bg-emerald-50 text-emerald-800';
  if (value === 'intermittent') return 'bg-amber-50 text-amber-800';
  if (value === 'erratic') return 'bg-orange-50 text-orange-800';
  if (value === 'lumpy') return 'bg-red-50 text-red-800';
  return 'bg-gray-100 text-gray-600';
}

function ClassValueBadge({
  dimension,
  value,
  label,
}: {
  dimension: ClassificationBreakdownProps['dimension'];
  value: string;
  label: string;
}) {
  if (dimension === 'abc_quantity' || dimension === 'abc_value') {
    return (
      <span className={`inline-flex min-w-[1.75rem] justify-center rounded px-2 py-0.5 text-sm font-bold ${abcBadgeClass(value)}`}>
        {label}
      </span>
    );
  }
  if (dimension === 'xyz_class') {
    return (
      <span className={`inline-flex min-w-[1.75rem] justify-center rounded px-2 py-0.5 text-sm font-bold ${xyzBadgeClass(value)}`}>
        {label}
      </span>
    );
  }
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${adiCvBadgeClass(value)}`}>
      {label}
    </span>
  );
}

export function ClassificationBreakdown({
  data,
  loading,
  viewType,
  dimension,
  onDimensionChange,
  activeClassValue,
  onApplyChartFilter,
}: ClassificationBreakdownProps) {
  const { t, i18n } = useTranslation();
  const { formatViewAmount } = useCurrency();
  const formatAmount = (value: number | null | undefined) =>
    value == null ? '—' : formatViewAmount(value, viewType);

  const formatClassValue = useCallback(
    (value: string) => {
      if (value === 'unknown') return t('forecastReview.classificationBreakdown.unknownClass');
      if (value === 'smooth') return t('forecastReview.classificationBreakdown.adiSmooth');
      if (value === 'intermittent') return t('forecastReview.classificationBreakdown.adiIntermittent');
      if (value === 'erratic') return t('forecastReview.classificationBreakdown.adiErratic');
      if (value === 'lumpy') return t('forecastReview.classificationBreakdown.adiLumpy');
      return value;
    },
    [t]
  );

  const dimensions: Array<{ id: typeof dimension; label: string }> = [
    { id: 'abc_quantity', label: t('forecastReview.classificationBreakdown.dimensionAbcQty') },
    { id: 'abc_value', label: t('forecastReview.classificationBreakdown.dimensionAbcValue') },
    { id: 'xyz_class', label: t('forecastReview.classificationBreakdown.dimensionXyz') },
    { id: 'adi_cv_class', label: t('forecastReview.classificationBreakdown.dimensionAdiCv') },
  ];

  const rows = data?.results ?? [];
  const totalForecast = useMemo(
    () => rows.reduce((sum, row) => sum + (row.forecast_total ?? 0), 0),
    [rows]
  );

  const forecastPeriodLabel = data?.forecast_period_label ?? '—';
  const lyPeriodLabel = data?.last_year_period_label ?? '—';
  const recentPeriodLabel = data?.recent_period_label ?? '—';
  const horizonCount = data?.horizon_periods ?? 1;

  const columns = useMemo(
    () => [
      {
        header: (
          <ColumnHeader
            title={t('forecastReview.classificationBreakdown.class')}
            subtitle={data?.dimension_label}
            align="left"
          />
        ),
        exportHeader: t('forecastReview.classificationBreakdown.class'),
        accessor: 'class_value',
        cell: (v: string) => (
          <div className="flex items-center gap-2">
            <ClassValueBadge dimension={dimension} value={v} label={formatClassValue(v)} />
            {onApplyChartFilter ? (
              <span className="hidden text-[10px] text-gray-400 sm:inline">
                {t('forecastReview.classificationBreakdown.filterHintShort')}
              </span>
            ) : null}
          </div>
        ),
      },
      {
        header: (
          <ColumnHeader title={t('forecastReview.classificationBreakdown.products')} align="right" />
        ),
        exportHeader: t('forecastReview.classificationBreakdown.products'),
        accessor: 'product_count',
        align: 'right' as const,
        numeric: true,
        cell: (v: number) => (
          <span className="font-medium text-gray-800">{formatUiNumber(v, undefined, i18n.language)}</span>
        ),
      },
      {
        header: (
          <ColumnHeader
            title={t('forecastReview.classificationBreakdown.forecast')}
            subtitle={forecastPeriodLabel}
            align="right"
          />
        ),
        exportHeader: t('forecastReview.classificationBreakdown.forecast'),
        accessor: 'forecast_total',
        align: 'right' as const,
        numeric: true,
        cell: (v: number) => <span className="font-medium text-gray-900">{formatAmount(v)}</span>,
      },
      {
        header: (
          <ColumnHeader
            title={t('forecastReview.classificationBreakdown.shareOfForecast')}
            align="right"
          />
        ),
        exportHeader: t('forecastReview.classificationBreakdown.shareOfForecast'),
        accessor: 'forecast_total',
        align: 'right' as const,
        numeric: true,
        cell: (v: number) => {
          if (!totalForecast) return <span className="text-gray-400">—</span>;
          const pct = (v / totalForecast) * 100;
          return <span className="text-gray-700">{pct.toFixed(1)}%</span>;
        },
      },
      {
        header: (
          <ColumnHeader
            title={t('forecastReview.classificationBreakdown.lastYear')}
            subtitle={lyPeriodLabel}
            align="right"
          />
        ),
        exportHeader: t('forecastReview.classificationBreakdown.lastYear'),
        accessor: 'last_year_total',
        align: 'right' as const,
        numeric: true,
        cell: (v: number) => formatAmount(v),
      },
      {
        header: (
          <ColumnHeader title={t('forecastReview.classificationBreakdown.varAa')} align="right" />
        ),
        exportHeader: t('forecastReview.classificationBreakdown.varAa'),
        accessor: 'variation_vs_last_year',
        align: 'right' as const,
        numeric: true,
        cell: (v: number | null) => <VariationBadge value={v} />,
      },
      {
        header: (
          <ColumnHeader
            title={t('forecastReview.classificationBreakdown.recent')}
            subtitle={recentPeriodLabel}
            align="right"
          />
        ),
        exportHeader: t('forecastReview.classificationBreakdown.recent'),
        accessor: 'recent_total',
        align: 'right' as const,
        numeric: true,
        cell: (v: number) => formatAmount(v),
      },
      {
        header: (
          <ColumnHeader title={t('forecastReview.classificationBreakdown.varRecent')} align="right" />
        ),
        exportHeader: t('forecastReview.classificationBreakdown.varRecent'),
        accessor: 'variation_vs_recent',
        align: 'right' as const,
        numeric: true,
        cell: (v: number | null) => <VariationBadge value={v} />,
      },
      {
        header: (
          <ColumnHeader title={t('forecastReview.classificationBreakdown.openAlerts')} align="right" />
        ),
        exportHeader: t('forecastReview.classificationBreakdown.openAlerts'),
        accessor: 'open_alerts_count',
        align: 'right' as const,
        numeric: true,
        cell: (v: number) =>
          v > 0 ? (
            <span className="inline-flex min-w-[1.5rem] justify-center rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-800">
              {v}
            </span>
          ) : (
            <span className="text-gray-400">0</span>
          ),
      },
    ],
    [
      t,
      i18n.language,
      viewType,
      dimension,
      formatClassValue,
      onApplyChartFilter,
      forecastPeriodLabel,
      lyPeriodLabel,
      recentPeriodLabel,
      totalForecast,
      data?.dimension_label,
    ]
  );

  const periodUnit =
    data?.granularity === 'week'
      ? horizonCount === 1
        ? t('forecastReview.kpis.periodUnitWeek')
        : t('forecastReview.kpis.periodUnitWeekPlural')
      : horizonCount === 1
        ? t('forecastReview.kpis.periodUnitMonth')
        : t('forecastReview.kpis.periodUnitMonthPlural');

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            {t('forecastReview.classificationBreakdown.title')}
          </h3>
          {data?.forecast_period_label ? (
            <p className="mt-0.5 max-w-3xl text-xs leading-relaxed text-gray-500">
              {t('forecastReview.classificationBreakdown.subtitle', {
                forecastPeriod: forecastPeriodLabel,
                count: horizonCount,
                unitLabel: periodUnit,
                lyPeriod: lyPeriodLabel,
                recentPeriod: recentPeriodLabel,
              })}
            </p>
          ) : null}
          {onApplyChartFilter ? (
            <p className="mt-1 text-xs text-[#1681ba]">{t('forecastReview.classificationBreakdown.filterHint')}</p>
          ) : null}
        </div>
        <div className="inline-flex rounded-md border border-gray-200 p-0.5 text-xs">
          {dimensions.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => onDimensionChange(d.id)}
              className={`rounded px-2.5 py-1 ${dimension === d.id ? 'bg-gray-100 font-medium text-gray-900' : 'text-gray-600'}`}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-500">
          {t('forecastReview.classificationBreakdown.loading')}
        </div>
      ) : !loading && rows.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-500">
          {t('forecastReview.classificationBreakdown.empty')}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          compact
          loading={loading}
          onRowClick={onApplyChartFilter}
          rowClassName={(row: ForecastReviewClassificationBreakdownRow) =>
            activeClassValue && row.class_value === activeClassValue
              ? 'bg-blue-50 ring-1 ring-inset ring-blue-200'
              : ''
          }
        />
      )}
    </div>
  );
}
